import { marked } from "marked";

/**
 * The documentation, written as Markdown in `docs/` at the repository root.
 *
 * GitBook's two conventions are what make it writable: the sidebar is
 * `docs/SUMMARY.md` (a list of links), and each page is a plain Markdown file a
 * contributor edits in a pull request. Nothing else is hand-kept - this module
 * reads those two things at build time, so a page cannot exist without being
 * listed, and a listing cannot point at a page that is not there.
 */

const RAW: Record<string, string> = import.meta.glob<string>("../../docs/**/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
});

const PAGES: Record<string, string> = {};

for (const [path, text] of Object.entries(RAW)) {
  PAGES[path.replace(/^.*\/docs\//, "")] = text;
}

/** The languages the pages are written in. `en` is the source of truth. */
export type DocsLang = "en" | "zh" | "vi" | "hi";

/** Where a page's translation lives: `one-thought.md` -> `zh/one-thought.md`. */
function translated(file: string, lang: DocsLang): string {
  return lang === "zh" ? `zh/${file}` : file;
}

/** Every page in reading order, flattened out of the sidebar. */
export interface DocsPage {
  title: string;
  file: string;
  section: string;
}

export interface DocsSection {
  title: string;
  pages: DocsPage[];
}

const LINK = /^-\s+\[([^\]]+)\]\(([^)]+)\)/;

const SECTION = /^##\s+(.+)$/;

/**
 * The sidebar: sections in the order SUMMARY.md writes them, with the titles of
 * the language being read. A missing translation falls back to the English
 * summary rather than to an empty sidebar.
 */
export function docsCatalog(lang: DocsLang = "en"): DocsSection[] {
  const sections: DocsSection[] = [];
  let current: DocsSection | null = null;
  const summary = PAGES[translated("SUMMARY.md", lang)] ?? PAGES["SUMMARY.md"] ?? "";

  for (const line of summary.split("\n")) {
    const section = line.match(SECTION);

    if (section !== null) {
      current = { title: section[1].trim(), pages: [] };
      sections.push(current);
      continue;
    }

    const page = line.match(LINK);

    if (page !== null && current !== null) {
      current.pages.push({ title: page[1], file: page[2], section: current.title });
    }
  }

  return sections;
}

export function docsFlat(lang: DocsLang = "en"): DocsPage[] {
  return docsCatalog(lang).flatMap((section) => section.pages);
}

/**
 * A page's raw Markdown in the requested language. A page with no translation
 * is served in English: a reader who chose 中文 should still be able to read a
 * page nobody has translated yet.
 */
export function docsText(file: string, lang: DocsLang = "en"): string {
  // the landing page of the language asked for is the last resort, not the
  // English one: a reader in Chinese should not land in English on a bad link
  return (
    PAGES[translated(file, lang)] ??
    PAGES[file] ??
    PAGES[translated("README.md", lang)] ??
    PAGES["README.md"] ??
    ""
  );
}

export function docsExists(file: string): boolean {
  return file in PAGES;
}

/**
 * Rendered pages, cached: the same page is asked for again on every navigation
 * and on every language switch, and the Markdown never changes while the page
 * is open.
 */
const rendered = new Map<string, string>();

export function docsHtml(file: string, lang: DocsLang = "en"): string {
  const key = `${lang}:${file}`;
  const cached = rendered.get(key);

  if (cached !== undefined) return cached;

  const html = marked.parse(docsText(file, lang), { gfm: true, async: false });

  // a page links to another page the way GitBook writes it - `the-85-tapes.md` -
  // so those hrefs have to become the reader's own routes, or a link inside the
  // docs walks out of the app
  const linked = html.replace(/href="([^"#/:]+\.md)"/g, 'href="#docs/$1"');
  const safe = linked.includes("<script") ? "This page is not renderable." : linked;

  rendered.set(key, safe);

  return safe;
}
